import { useState, useDeferredValue } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import Input from '../../../../components/Input';
import Checkbox from '../../../../components/Checkbox';
import { Tag } from '../../../../components/Tag';
import { MdCancel, MdKeyboardArrowDown } from 'react-icons/md';

interface Props {
  values: string[];
  placeholder: string;
  options: string[];
  onUpdate: (values: string[]) => void;
  searchable?: boolean;
  align?: 'start' | 'end';
}

export default function MultiSelectPopover({
  values,
  placeholder,
  options,
  onUpdate,
  searchable = false,
  align = 'start',
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const selectedValues = values.filter(Boolean);

  const filteredOptions = searchable
    ? options.filter((option) => option.toLowerCase().includes(deferredQuery.toLowerCase()))
    : options;

  const handleCheckboxChange = (option: string, checked: boolean) => {
    const newValues = [...values];
    const index = newValues.indexOf(option);

    if (checked && index === -1) {
      newValues.push(option);
    } else if (!checked && index !== -1) {
      newValues.splice(index, 1);
    }

    onUpdate(newValues);
  };

  const removeValue = (valueToRemove: string) => {
    onUpdate(values.filter((value) => value !== valueToRemove));
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative flex h-xxl w-full cursor-pointer items-center justify-between rounded-sm border border-solid border-border bg-white p-2">
          <div className="flex flex-1 gap-2 overflow-x-auto pr-2 text-nowrap">
            {selectedValues.length > 0 ? (
              selectedValues.map((value) => (
                <div key={value} className="shrink-0">
                  <Tag color="default" className="flex items-center gap-1">
                    {value}
                    <button
                      type="button"
                      className="flex size-5 items-center justify-center rounded-full border-none bg-transparent p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeValue(value);
                      }}
                    >
                      <MdCancel className="fill-main text-lg opacity-50" />
                    </button>
                  </Tag>
                </div>
              ))
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          {selectedValues.length > 0 && (
            <div className="pointer-events-none absolute inset-y-2 right-8 w-8 bg-gradient-to-l from-white to-transparent" />
          )}
          <MdKeyboardArrowDown
            className={`text-xl text-main transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align={align} className="z-[110] w-[724px] p-4">
        <div className="space-y-4">
          {searchable && (
            <Input
              search
              className="w-full"
              placeholder="フリーワードで検索"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}

          <div className="max-h-[264px] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              {filteredOptions.map((option) => (
                <Checkbox
                  key={option}
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(checked) => handleCheckboxChange(option, checked)}
                >
                  <span className="font-normal">{option}</span>
                </Checkbox>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
